import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { ArrowLeft, Building2, ClipboardList, Download, EyeOff, Loader2, MessageSquareText, PencilLine, RotateCcw, Zap } from "lucide-react";
import { Flash } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlashHero } from "@/components/flash/FlashHero";
import { FlashScorecard } from "@/components/flash/FlashScorecard";
import { FlashHeatmap } from "@/components/flash/FlashHeatmap";
import { FlashEntryDialog } from "@/components/flash/FlashEntryDialog";
import { FlashFormsDialog } from "@/components/flash/FlashFormsDialog";
import { FlashProfileDialog } from "@/components/flash/FlashProfileDialog";
import { FlashNotesSheet } from "@/components/flash/FlashNotesSheet";
import { FlashResetDialog } from "@/components/flash/FlashResetDialog";

const todayLocal = () => format(new Date(), "yyyy-MM-dd");

const Header = ({ data, date, setDate, onEntry, onForms, onProfile, onReset, onPdf, onNotesPdf, downloading }) => (
  <header className="flex flex-col gap-4 border-b border-slate-200 pb-5" data-testid="flash-header">
    <div className="flex items-center gap-3 min-w-0">
      <Link to="/" className="p-2 rounded-lg hover:bg-slate-200/60 text-slate-600 transition-colors" data-testid="flash-back-link">
        <ArrowLeft className="w-4 h-4" />
      </Link>
      <div className="w-10 h-10 rounded-xl bg-sky-600 text-white flex items-center justify-center shadow-sm shrink-0">
        <Zap className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-[0.25em] text-sky-700 uppercase">Daily Flash</div>
        <div className="font-heading font-bold text-slate-900 leading-tight">Documentation Timeliness</div>
        <button onClick={onProfile} className="text-xs text-slate-500 hover:text-sky-700 truncate max-w-full transition-colors" data-testid="flash-facility-name">
          {data?.facility_name || "Add hospital name"}
        </button>
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-44 font-mono bg-white" data-testid="date-picker-button" />
      <Button variant="outline" onClick={onProfile} className="bg-white" data-testid="btn-open-profile-dialog">
        <Building2 className="w-4 h-4 mr-2" /> Profile
      </Button>
      <Button variant="outline" onClick={onReset} className="bg-white text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="btn-open-reset-dialog">
        <RotateCcw className="w-4 h-4 mr-2" /> Reset
      </Button>
      <Button variant="outline" onClick={onForms} className="bg-white" data-testid="btn-open-manage-forms-dialog">
        <ClipboardList className="w-4 h-4 mr-2" /> Forms
      </Button>
      <Button variant="outline" onClick={onEntry} className="bg-white border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="btn-open-data-entry-dialog">
        <PencilLine className="w-4 h-4 mr-2" /> Enter metrics
      </Button>
      <Button variant="outline" onClick={onNotesPdf} disabled={!!downloading} className="bg-white" title="Download all owner notes for this month" data-testid="btn-export-notes-pdf">
        {downloading === "notes" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquareText className="w-4 h-4 mr-2" />} Notes log
      </Button>
      <Button onClick={onPdf} disabled={!!downloading} className="bg-[#0f172a] hover:bg-slate-800" data-testid="btn-export-pdf">
        {downloading === "flash" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download PDF
      </Button>
    </div>
  </header>
);

const Scorecards = ({ data, onNotes, onForms }) => (
  <section className="space-y-4">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="font-heading font-semibold text-lg text-slate-900">Form scorecards</h2>
        <p className="text-sm text-slate-500">{data.month_label} month-to-date vs {data.prior_month_label} · the black tick marks each goal</p>
      </div>
      {data.hidden_forms.length > 0 && (
        <button onClick={onForms} className="inline-flex items-center gap-1.5 text-xs text-slate-500 border border-dashed border-slate-300 rounded-full px-3 py-1 hover:text-sky-700 hover:border-sky-300 transition-colors" data-testid="hidden-forms-chip">
          <EyeOff className="w-3.5 h-3.5" /> {data.hidden_forms.length} hidden from dashboard & PDF
        </button>
      )}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="scorecards-container">
      {data.forms.map((f, i) => <FlashScorecard key={f.id} form={f} index={i} onNotes={onNotes} />)}
      {data.forms.length === 0 && <div className="text-sm text-slate-500 col-span-full">No forms shown. Use Forms to add or unhide one.</div>}
    </div>
  </section>
);

export default function DailyFlash() {
  const [date, setDate] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [allForms, setAllForms] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [formsOpen, setFormsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [notesForm, setNotesForm] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(
    () => Promise.all([Flash.dashboard(date), Flash.forms()])
      .then(([d, f]) => { setData(d); setAllForms(f); })
      .catch(() => toast.error("Could not load the flash dashboard")),
    [date],
  );
  useEffect(() => { load(); }, [load]);

  const download = async (kind, fn) => {
    setDownloading(kind);
    try { await fn(); } catch { toast.error("Download failed"); } finally { setDownloading(null); }
  };
  const pdf = () => download("flash", () => Flash.pdf(date));
  const notesPdf = () => download("notes", () => Flash.notesPdf(date.slice(0, 7)));

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        <Header data={data} date={date} setDate={setDate} onEntry={() => setEntryOpen(true)} onForms={() => setFormsOpen(true)} onProfile={() => setProfileOpen(true)} onReset={() => setResetOpen(true)} onPdf={pdf} onNotesPdf={notesPdf} downloading={downloading} />
        {!data ? (
          <div className="h-64 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading flash…</div>
        ) : (
          <>
            <FlashHero data={data} />
            <Scorecards data={data} onNotes={setNotesForm} onForms={() => setFormsOpen(true)} />
            <FlashHeatmap data={data} />
          </>
        )}
      </main>
      <FlashEntryDialog open={entryOpen} onOpenChange={setEntryOpen} initialDate={date} forms={allForms} onSaved={load} />
      <FlashFormsDialog open={formsOpen} onOpenChange={setFormsOpen} forms={allForms} onChanged={load} />
      <FlashProfileDialog open={profileOpen} onOpenChange={setProfileOpen} onSaved={load} />
      <FlashResetDialog open={resetOpen} onOpenChange={setResetOpen} onDone={load} />
      <FlashNotesSheet form={notesForm} onOpenChange={(o) => !o && setNotesForm(null)} onChanged={load} />
    </div>
  );
}
